import type {
  RegistrationNumberAlertFormState,
  TravelledVehicleType,
} from "../../../services/update-form/registrationNumberAlert";
import type { TravelledVehicle } from "../../graphql/fragments/gql/TravelledVehicle";
import type { VehicleType } from "../../graphql/fragments/gql/VehicleType";

export const getAppliedDimension = (
  dimension: number,
  previousDimension: number | undefined,
): boolean => dimension !== 0 && dimension !== previousDimension;

export const registrationNumberAlertEqualsValue = (
  registrationNumberAlert: RegistrationNumberAlertFormState | undefined,
  field: FieldName,
  editedValue: number | undefined,
) => {
  if (
    (field === "length" || field === "height") &&
    registrationNumberAlert?.result &&
    editedValue
  ) {
    return registrationNumberAlert.result[field] === editedValue;
  } else {
    return false;
  }
};

export function getRegistrationNumberAlertFormStateKey(
  field: FieldName,
): keyof Partial<
  Pick<
    RegistrationNumberAlertFormState,
    | "appliedLength"
    | "appliedHeight"
    | "appliedGoods"
    | "appliedLoadingListMessage"
  >
> {
  switch (field) {
    case "length":
      return "appliedLength";
    case "height":
      return "appliedHeight";
    case "goodsWeight":
      return "appliedGoods";
    case "loadingNote":
      return "appliedLoadingListMessage";
  }
}

export type FieldName = "length" | "height" | "goodsWeight" | "loadingNote";
export type ReadableFieldName =
  | "length"
  | "height"
  | "goods weight"
  | "loading note";

export function getReadableFieldName(field: FieldName): ReadableFieldName {
  switch (field) {
    case "length":
      return "length";
    case "height":
      return "height";
    case "goodsWeight":
      return "goods weight";
    case "loadingNote":
      return "loading note";
  }
}

export function getRegistrationNumberAlertAppliedProps(
  field: FieldName,
  registrationNumberAlert: RegistrationNumberAlertFormState | null | undefined,
): {
  showArrivalNotepad: boolean;
  showTravelledVehicle: boolean;
  arrivalNotepadApplied: boolean;
} {
  const arrivalNotepadApplied = Boolean(
    registrationNumberAlert?.appliedArrivalNotepad,
  );

  if (field === "length") {
    const showArrivalNotepad =
      arrivalNotepadApplied && Boolean(registrationNumberAlert?.appliedLength);
    const showTravelledVehicle = Boolean(
      registrationNumberAlert?.appliedLength,
    );

    return {
      showArrivalNotepad,
      showTravelledVehicle,
      arrivalNotepadApplied,
    };
  }

  if (field === "height") {
    const showArrivalNotepad =
      arrivalNotepadApplied && Boolean(registrationNumberAlert?.appliedHeight);
    const showTravelledVehicle = Boolean(
      registrationNumberAlert?.appliedHeight,
    );

    return {
      showArrivalNotepad,
      showTravelledVehicle,
      arrivalNotepadApplied,
    };
  }

  if (field === "goodsWeight") {
    const showArrivalNotepad =
      arrivalNotepadApplied && Boolean(registrationNumberAlert?.appliedGoods);
    const showTravelledVehicle = Boolean(registrationNumberAlert?.appliedGoods);

    return {
      showArrivalNotepad,
      showTravelledVehicle,
      arrivalNotepadApplied,
    };
  }

  if (field === "loadingNote") {
    const showArrivalNotepad =
      arrivalNotepadApplied &&
      Boolean(registrationNumberAlert?.appliedLoadingListMessage);
    const showTravelledVehicle = Boolean(
      registrationNumberAlert?.appliedLoadingListMessage,
    );

    return {
      showArrivalNotepad,
      showTravelledVehicle,
      arrivalNotepadApplied,
    };
  }

  return {
    showArrivalNotepad: false,
    showTravelledVehicle: false,
    arrivalNotepadApplied,
  };
}

export const hasArrivalNotepad = (
  registrationNumberAlert: RegistrationNumberAlertFormState | undefined | null,
  travelledVehicleType: TravelledVehicleType,
): boolean =>
  Boolean(
    registrationNumberAlert?.appliedArrivalNotepad &&
    registrationNumberAlert.travelledVehicleType === travelledVehicleType,
  );

interface TravelledVehicleRegistrationNumberAlertFormState extends RegistrationNumberAlertFormState {
  result: TravelledVehicle;
  vehicleType: VehicleType;
}

export const hasTravelledVehicle = (
  registrationNumberAlert: RegistrationNumberAlertFormState | undefined | null,
  travelledVehicleType: TravelledVehicleType,
  vehicleType: VehicleType | null | undefined,
): registrationNumberAlert is TravelledVehicleRegistrationNumberAlertFormState =>
  Boolean(
    registrationNumberAlert &&
    registrationNumberAlert.travelledVehicleType === travelledVehicleType &&
    registrationNumberAlert.vehicleType &&
    vehicleType &&
    vehicleType.code !== registrationNumberAlert.vehicleType.code &&
    registrationNumberAlert.vehicleType &&
    registrationNumberAlert.result,
  );
export const getRegistrationNumberAlertProps = (
  registrationNumberAlert: RegistrationNumberAlertFormState | undefined | null,
  travelledVehicleType: TravelledVehicleType,
  vehicleType: VehicleType | null | undefined,
): {
  hasAppliedTravelledVehicle: boolean;
  hasArrivalNotepad: boolean;
  hasTravelledVehicle: boolean;
} => {
  return {
    hasAppliedTravelledVehicle: Boolean(
      registrationNumberAlert?.appliedTravelledVehicle,
    ),
    hasTravelledVehicle: hasTravelledVehicle(
      registrationNumberAlert,
      travelledVehicleType,
      vehicleType,
    ),
    hasArrivalNotepad: hasArrivalNotepad(
      registrationNumberAlert,
      travelledVehicleType,
    ),
  };
};
